import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Input from "src/components/ui/Input";
import Dropdown from "src/components/ui/Dropdown";
import FilterSelect from "src/components/ui/FilterSelect";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantUserActions from "src/redux/slices/tenantUserSlice";
import * as tenantActions from "src/redux/slices/tenantSlice";
import * as tenantDepartmentActions from "src/redux/slices/tenantDepartmentSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import { genderOptions } from "src/constants/common";
import { toast } from "react-toastify";

interface UserLocationState {
  user?: any;
}

interface TenantUserFormProps {
  tenantUserId?: string;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const TenantUserForm: React.FC<TenantUserFormProps> = ({
  tenantUserId,
  onCancel,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [requestedList, setRequestedList] = useState(false);

  const dispatch = useAppDispatch();
  const { createTenantUser, editTenantUser, tenantUserList } = useAppSelector(
    (state) => state.tenantUser
  );
  const { tenantRoleList } = useAppSelector((state) => state.tenant);
  const { tenantDepartmentList } = useAppSelector(
    (state) => state.tenantDepartment
  );
  const { singular: warehouseLabel } = useWarehouseLabel();
  const { warehouseList } = useAppSelector((state) => state.warehouse);

  const locationState = location.state as UserLocationState | undefined;
  const userFromState = locationState?.user || null;

  const userFromList = useMemo(() => {
    if (!tenantUserId) return null;
    return (
      tenantUserList?.data?.data?.find((item) => item?._id === tenantUserId) ||
      null
    );
  }, [tenantUserId, tenantUserList?.data?.data]);

  const activeUser = userFromState || userFromList;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [role, setRole] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [joiningDate, setJoiningDate] = useState("");
  const [salary, setSalary] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!activeUser) return;
    setFirstName(activeUser.firstName || "");
    setLastName(activeUser.lastName || "");
    setPhone(activeUser.phone || "");
    setGender(activeUser.gender || "");
    setRole(
      typeof activeUser.role === "string"
        ? activeUser.role
        : activeUser.role?._id || ""
    );
    setDepartmentId(
      typeof activeUser.departmentId === "string"
        ? activeUser.departmentId
        : activeUser.departmentId?._id || ""
    );
    setWarehouseId(
      typeof activeUser.warehouseId === "string"
        ? activeUser.warehouseId
        : activeUser.warehouseId?._id || ""
    );
    setJoiningDate(
      activeUser.joiningDate ? String(activeUser.joiningDate).slice(0, 10) : ""
    );
    setSalary(activeUser.salary != null ? String(activeUser.salary) : "");
    setEmail(activeUser.email || "");
    setPassword("");
  }, [activeUser]);

  useEffect(() => {
    dispatch(tenantActions.getTenantRoleListRequest({}));
    dispatch(
      tenantDepartmentActions.getTenantDepartmentListRequest({
        page: 1,
        limit: 100,
      })
    );
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (tenantUserId && !activeUser && !requestedList) {
      dispatch(
        tenantUserActions.getTenantUserListRequest({
          page: 1,
          limit: 1000,
        })
      );
      setRequestedList(true);
    }
  }, [tenantUserId, activeUser, requestedList, dispatch]);

  useEffect(() => {
    if (tenantUserId && requestedList && !activeUser && tenantUserList?.data) {
      toast.error("User not found");
      if (onCancel) {
        onCancel();
      } else {
        navigate("/app/team-management/user-list");
      }
    }
  }, [
    tenantUserId,
    requestedList,
    activeUser,
    tenantUserList?.data,
    navigate,
    onCancel,
  ]);

  const handleClose = () => {
    dispatch(tenantUserActions.resetCreateTenantUser());
    dispatch(tenantUserActions.resetEditTenantUser());
    if (onCancel) {
      onCancel();
    } else {
      navigate("/app/team-management/user-list");
    }
  };

  const handleSuccess = () => {
    dispatch(tenantUserActions.resetCreateTenantUser());
    dispatch(tenantUserActions.resetEditTenantUser());
    if (onSuccess) {
      onSuccess();
      return;
    }
    navigate("/app/team-management/user-list");
  };

  useEffect(() => {
    if (createTenantUser.data) {
      toast.success("Staff member created successfully");
      handleSuccess();
    } else if (createTenantUser.error) {
      toast.error(`Error: ${createTenantUser.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTenantUser]);

  useEffect(() => {
    if (editTenantUser.data) {
      toast.success("Staff member updated successfully");
      handleSuccess();
    } else if (editTenantUser.error) {
      toast.error(`Error: ${editTenantUser.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTenantUser]);

  const roleOptions = useMemo(
    () =>
      tenantRoleList?.data?.data?.map((r: any) => ({
        label: r?.name || "",
        value: r?._id || "",
      })) || [],
    [tenantRoleList?.data?.data]
  );

  const departmentOptions = useMemo(
    () =>
      tenantDepartmentList?.data?.data?.map((d: any) => ({
        label: d?.name || "",
        value: d?._id || "",
      })) || [],
    [tenantDepartmentList?.data?.data]
  );

  const warehouseOptions = useMemo(
    () =>
      warehouseList?.data?.data?.map((w: any) => ({
        label: w?.name || "",
        value: w?._id || "",
      })) || [],
    [warehouseList?.data?.data]
  );

  const emailValid = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValid =
    !!firstName.trim() && !!role && !!departmentId && emailValid;

  const loading = createTenantUser.loading || editTenantUser.loading;

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid) return;

    const data: Record<string, any> = {
      firstName: firstName.trim(),
      lastName: lastName.trim() || undefined,
      phone: phone.trim() || undefined,
      gender: gender || undefined,
      role,
      departmentId,
      warehouseId: warehouseId || null,
      joiningDate: joiningDate || undefined,
      salary: salary ? Number(salary) : undefined,
      email: email.trim() || undefined,
    };
    if (password.trim()) data.password = password.trim();

    if (tenantUserId) {
      dispatch(
        tenantUserActions.editTenantUserRequest({ ...data, _id: tenantUserId })
      );
      return;
    }
    dispatch(tenantUserActions.createTenantUserRequest(data));
  };

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Personal info
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={touched && !firstName.trim()}
            helperText={touched && !firstName.trim() ? "First name is required" : undefined}
          />
          <Input
            label="Last name"
            optional
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <Input
            label="Phone"
            optional
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Dropdown
            label="Gender"
            optional
            value={gender}
            options={genderOptions}
            onChange={setGender}
          />
        </div>
      </Card>

      <Card>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Work info
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FilterSelect
            variant="boxed"
            label="Designation"
            required
            value={role}
            onChange={setRole}
            allLabel="Select designation"
            options={roleOptions}
            error={touched && !role}
          />
          <FilterSelect
            variant="boxed"
            label="Department"
            required
            value={departmentId}
            onChange={setDepartmentId}
            allLabel="Select department"
            options={departmentOptions}
            error={touched && !departmentId}
          />
          <FilterSelect
            variant="boxed"
            label={warehouseLabel}
            value={warehouseId}
            onChange={setWarehouseId}
            allLabel="Not assigned"
            options={warehouseOptions}
          />
          <Input
            label="Joining date"
            type="date"
            optional
            value={joiningDate}
            onChange={(e) => setJoiningDate(e.target.value)}
          />
          <Input
            label="Salary"
            type="number"
            optional
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
          />
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Optional. When set, this person's {warehouseLabel.toLowerCase()}-picking
          screens (Sale, Purchase, Stock Adjustment, Transfers, Reports) lock
          to this {warehouseLabel.toLowerCase()} instead of offering a free choice.
        </p>
      </Card>

      <Card>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Login access (optional)
        </h3>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Only needed if this person should sign in to Invyntra.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Email"
            optional
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={touched && !emailValid}
            helperText={touched && !emailValid ? "Invalid email format" : undefined}
          />
          <Input
            label="Password"
            type="password"
            optional
            placeholder={tenantUserId ? "Leave blank to keep current" : "Leave blank for no login yet"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </Card>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} isLoading={loading} disabled={loading}>
          {tenantUserId ? "Update Staff" : "Add Staff"}
        </Button>
      </div>
    </div>
  );
};

export default TenantUserForm;
